import { Flip, toast, type ToastOptions, type TypeOptions, type UpdateOptions, Zoom } from "react-toastify";

import { MS_IN_SECOND } from "./constants";

export const PushSuccessNotification = (notification: string, info?: string) =>
	PushNotification(notification, "success", info);
export const PushWarningNotification = (notification: string, info?: string) =>
	PushNotification(notification, "warning", info);
export const PushErrorNotification = (notification: string, info?: string) =>
	PushNotification(notification, "error", info);

/**
 * Shows a toast notification
 * @param notification text to display
 * @param notificationType toast variant; defaults to a warning
 * @param info not displayed yet
 */
export function PushNotification(notification: string, notificationType?: TypeOptions, info?: string) {
	PushNotificationInternal(notification, notificationType, info);

	return {
		type: "UNKNOWN",
	};
}

/**
 * Shows a toast notification, deduplicated by text and type except while processing
 * @param notification text to display
 * @param notificationType toast variant; defaults to a warning
 * @param _info not displayed yet
 * @returns the toast id
 */
export function PushNotificationInternal(
	notification: string,
	notificationType?: TypeOptions | "processing",
	_info?: string,
) {
	const options: ToastOptions = {
		autoClose: 5 * MS_IN_SECOND,
		toastId: notification + notificationType,
	};
	switch (notificationType) {
		case "processing":
			options.autoClose = 60 * MS_IN_SECOND;
			options.closeOnClick = false;
			options.closeButton = false;
			options.hideProgressBar = true;
			options.pauseOnHover = false;
			options.toastId = null;
			return toast(notification, options);
		case "info":
			return toast.info(notification, options);
		case "success":
			return toast.success(notification, options);
		case "error":
			//TODO: include info
			return toast.error(notification, options);
		case "warning":
		default:
			return toast.warn(notification, options);
	}
}

/**
 * Starts a "Processing..." notification, that can be updated by calling the return functions
 * @param message message to display while processing
 */
export function PushProcessingNotification(message = "Processing..."): ProcessingNotification {
	const processToast = PushNotificationInternal(message, "processing");
	return {
		/**If processing next step */
		processing: (text) => toast.update(processToast, { render: text, transition: Zoom }),
		success: (text) => toast.update(processToast, completeOptions("success", text)),
		error: (text) => toast.update(processToast, completeOptions("error", text)),
		info: (text) => toast.update(processToast, completeOptions("info", text)),
	};
}

function completeOptions(type: TypeOptions, render: string): UpdateOptions {
	return {
		type,
		render,
		autoClose: 5 * MS_IN_SECOND,
		closeButton: true,
		closeOnClick: true,
		pauseOnHover: true,
		hideProgressBar: false,
		transition: Flip,
	};
}

export interface ProcessingNotification {
	processing: (message: string) => void;
	success: (message: string) => void;
	error: (message: string, ex?: any) => void;
	info: (message: string) => void;
}
